<template>
  <div
    :style="`border-radius: 35px; background-color: ${$vuetify.theme.themes.light.secondary}; position: relative`"
  >
    <!-- we replace v-app with a custom div, as .v-application--wrap incorrectly sets height: 100vh.. oof  -->
    <!-- only caveat found so far is that click-outside directive for v-menu etc only works on this main div, not full page. -->
    <div
      data-app
      class="widget v-application v-application--is-ltr theme--light"
    >
      <v-main>
        <v-progress-linear
          v-if="pageLoading.value"
          indeterminate
          style="height: 10px"
          color="red"
        ></v-progress-linear>
        <div
          ref="bottom"
          class="bottom-div-container"
          :style="{ color: textColor, 'margin-left': '20px' }"
        >
          <p v-html="bottom"></p>
        </div>
        <div
          class="flex-box-left-aligned-horizontal"
          style="padding: 10px; width: 100%"
        >
          <Searchbar
            style="flex: 1; margin-right: 5px"
            ref="searchbar"
            :destinations="config.destinations"
            :forceVerticalDisplayMode="forceVerticalDisplayMode"
            :params="searchParams"
            :queryHandler="openCleverRoute"
            :loading="pageLoading.value"
            :hideDestination="hideDestination"
            embedPositioningHack
            :updateIncludeIntermodal="updateIncludeIntermodal"
            :updateMinutesCongestion="updateMinutesCongestion"
            :updateMinutesParking="updateMinutesParking"
            :extendedOptionsPanelOpened="extendedOptionsPanelOpened"
            :locInputChanged="locInputChanged"
            :showCRlogo="true"
            :searchBarWithBorder="false"
            :config="config"
          />
          <div style="width: 70%; height: 100%" v-if="forceVerticalDisplayMode">
            <Map
              :height="adaptMapHeight() + 'px'"
              :checkedProvidersDict="checkedProvidersDict"
              :originLonLatStr="searchParams.originLonLat"
              :destinationLonLatStr="searchParams.destinationLonLat"
              :gesture-handling="$vuetify.breakpoint.mdAndDown"
              :transports="config.transports"
              :arrival="String(searchParams.arrival)"
              :departure="String(searchParams.departure)"
              :mapStyle="'margin-right: 12px; margin-top: 12px;'"
              :mapRouteColor="config.frontend.mapRouteColor"
              :mapIconColor="config.frontend.mapIconColor"
            />
          </div>
        </div>
      </v-main>
    </div>
  </div>
</template>

<script>
import {
  clone,
  fromQueryString,
  toQueryString,
} from "@norwin/javascript-is-bad";
import Vue from "vue";
import { fetchRouteParamsValid } from "./api";
import Map from "./components/Map.vue";
import MapOverviewToolbar from "./components/MapOverviewToolbar.vue";
import Searchbar from "./components/Searchbar.vue";
import { config, frontendConfig } from "./config";
import "./styles/global.css";
import * as utils from "./utils";
import { useConfigStore } from "./store/config-store";
import { storeToRefs } from "pinia";

export default Vue.extend({
  name: "Widget",

  components: {
    Searchbar,
    MapOverviewToolbar,
    Map,
  },

  data() {
    return {
      pageLoading: { value: false },
      height: 0,
      // offset für the additional height of the expansion panel with the extended options in the open state
      optionsPanelHeightOffset: 100,
      useLogoLink: true,
      hideDestination: false,
      searchParams: {
        origin: "",
        arrival: "",
        originLonLat: "",
        destinationLonLat: "",
        destinationId: "",
        departure: "",
        destination: undefined,
        dayTicket: false,
        includeIntermodal: undefined,
        minutesCongestion: 0,
        minutesParking: 0,
        useDepartureTimeForJourneyThere: false,
      },
      config: {
        frontend: {
          widgetOpenSameWindow: false,
          showOverviewMap: false,
          mapRouteColor: "green",
          mapIconColor: "green",
        },
        destinations: [{ id: "default" }],
        transports: [],
      },

      // additional content, used as filler for overflow area
      bottom: "",
      textColor: "white",
      extendedOptionsPanelOffset: 0,
      // checkedTransportsDict: getInitCheckedTransports(),
      checkedProvidersDict: this.getInitCheckedProvidersWrapper(),
    };
  },
  methods: {
    updateCheckedProviders(key, val) {
      this.checkedProvidersDict[key] = val;
    },
    getInitCheckedProvidersWrapper() {
      if (!this.config?.transports) {
        return {};
      }
      return getInitCheckedProviders(this.config);
    },
    // applies config as fetched from the cleverroute api
    async applyFrontendConfig() {
      this.config = await frontendConfig();
      // load full config asynchronously and disable search-button as long as it is loading

      const boundFunc = utils.applyConfig.bind(this);
      boundFunc();
      const { pageTitle } = this.config.frontend;

      document.title = pageTitle; //`CleverRoute - Ihre Anreise ${placename}`;
    },
    async applyFullConfig() {
      this.config = await config();
      // after fullConfig is loaded set pageLoading in store to false
      useConfigStore().setPageLoading(false);
    },
    updateIncludeIntermodal(includeIntermodal) {
      this.searchParams.includeIntermodal = includeIntermodal;
    },
    updateMinutesCongestion(minCongestion) {
      this.searchParams.minutesCongestion = minCongestion;
    },
    updateMinutesParking(minParking) {
      this.searchParams.minutesParking = minParking;
    },
    // emits height via postMessage protocol to the parent window, so it can size the iframe correctly
    async emitHeight() {
      await this.$nextTick(); // wait for re-layout to apply

      // const mapHeight = (this.config.frontend.showOverviewMap && this.$refs.overviewMap?.getBoundingClientRect().height)
      //   ? this.$refs.overviewMap?.getBoundingClientRect().height
      //   : 0;
      const height = this.computeHeight();

      window.parent.postMessage({ type: "cleverroute_height", height }, "*");
      return height;
    },

    async openCleverRoute() {
      if (!fetchRouteParamsValid(this.searchParams, this.config.destinations))
        return;
      const p = clone(this.searchParams);

      // set parameter for journey there time orientation e.g. arrival or departure time
      const journeyTimeOrientation = storeToRefs(
        useConfigStore()
      ).metricToJourneyThereTimeOrientation;
      p.useDepartureTimeForJourneyThere =
        journeyTimeOrientation.value === "arrival" ? false : true;
      // open cleverroute main page in same tab if explicitly specified in config, otherwise in new tab
      if (this.config.frontend.widgetOpenSameWindow) {
        window.parent.location.href = `../?${toQueryString(p)}`;
      } else {
        window.open(`../?${toQueryString(p)}`);
      }
    },

    parseBottomText(bottomText) {
      if (!this.useLogoLink) return bottomText;
      return bottomText.replace(
        /cleverroute/i,
        '<a href="/" target="_blank"><img style="height: 1em; vertical-align: middle" src="/logo/logo_schmal.png" title="CleverRoute" /></a>'
      );
    },

    parseURLParams() {
      const p = fromQueryString();
      // if useLogoLink is not present in the parameter or if it is set as false, then assign the instance variable accordingly
      this.useLogoLink =
        p.useLogoLink === undefined || JSON.parse(p.useLogoLink) === false
          ? false
          : true;

      this.hideDestination = p.hideDestination === "true";
      // parse Url params related to locality and extended settings like congestion and parking time
      const boundFunc = utils.parseURLParams.bind(this, p);
      boundFunc();
      if (isNaN(this.searchParams.departure))
        throw new Error("departure date is invalid");
      if (isNaN(this.searchParams.arrival))
        throw new Error("arrival date is invalid");

      // styling
      this.bottom = this.parseBottomText(p.bottomText) || "";
      this.textColor = p.textColor || this.textColor;
    },
    locInputChanged(geocodeObj, inputKey) {
      utils.locInputChangedExternal.bind(this, geocodeObj, inputKey)();
    },
    extendedOptionsPanelOpened(opened) {
      // updating height if the panel for extended options is opened or closed
      this.extendedOptionsPanelOffset = opened
        ? this.optionsPanelHeightOffset
        : 0;
      // this.panelOpened = opened;
      this.emitHeight();
      // this.adaptMapHeight();
    },

    computeHeight() {
      const searchHeight = this.$refs.searchbar?.$el?.clientHeight;
      const bottomHeight = this.$refs.bottom?.getBoundingClientRect().height; // NOTE: min-height to accomodate for overflow is set via CSS.
      // Paddng-bottom of the searchbar so we don't use a hardcoded height for searchbar to consider some spaces in the bottom
      const paddingBottom = 40;
      const height =
        searchHeight +
        bottomHeight +
        this.extendedOptionsPanelOffset +
        paddingBottom;
      return height;
    },
    adaptMapHeight() {
      this.height = 0.8 * this.computeHeight();
      return this.height;
    },
  },
  computed: {
    forceVerticalDisplayMode() {
      const p = fromQueryString();
      const showOverviewMap = p.showOverviewMap;
      // if map should be shown, then force vertical display mode for the seachbar
      const res =
        showOverviewMap != undefined
          ? JSON.parse(showOverviewMap)
          : this.config.frontend.showOverviewMap
          ? true
          : false;
      return res;
    },
  },
  watch: {},
  async mounted() {},
  async created() {
    // initiating reactive variable for pageloading
    this.pageLoading = storeToRefs(useConfigStore()).pageLoading;
    useConfigStore().setPageLoading(true);

    try {
      await this.applyFrontendConfig();
      this.parseURLParams();
      await this.applyFullConfig();

      /* if a predefined destination is passed in URL then retrieve its coordinates by its name so the marker 
      can be set in the map automatically on loading
      adding also some delay, so its bounds are shown in the map after setGermanyBounds */
      setTimeout(
        () => utils.parseDestinationByDestinationName.bind(this)(),
        300
      );

      this.emitHeight();
      window.addEventListener("resize", () => {
        // HACK: as relayouting can be rather slow, we need
        // to wait a bit until we measure the correct height
        setTimeout(() => this.emitHeight(), 500);
        this.emitHeight();
        // this.adaptMapHeight();
      });
    } catch (err) {
      console.error(err);
    }
  },
});
</script>

<style scoped>
/* HACK: this to make the v-menu attach (roughly) at the right place even when the widget is not at the top of page.
     Vuetify as a whole is not compatible with this usecase (wtf) (see v-app workaround above), see also
     https://github.com/vuetifyjs/vuetify/issues/7703
     This workaround is needed, as I couldnt get the attach prop on v-combobox to work as intended.. */
.widget .v-autocomplete__content {
  position: sticky !important;
}

body {
  background: transparent;
}

.widget.v-application {
  /* for the workaround above to work, the v-menu parent needs to be dislpay block. */
  display: block;
  background: transparent;
}

.bottom-div-container {
  margin: 10px;
  min-height: 30px; /* make enough room for date picker modal overflow */
  font-size: larger;
}
</style>
